/**
 * Turning a running game into a save, and back.
 *
 * Kept separate from the byte format so both halves stay testable: this file
 * knows what a game is made of, saveGame.ts knows how bytes are laid out, and
 * platform/saveStore.ts knows where they go. A round-trip test can drive all
 * three without a browser.
 */

import { LOAD_REACH, type World } from "../world/world.js";
import type { PlayerController } from "../player/controller.js";
import type { Inventory } from "../inventory/inventory.js";
import type { DayNightCycle } from "../world/daynight.js";
import { migrateItemId } from "../block/items.js";
import { isSolid } from "../block/registry.js";
import { makePickup, PICKUP_LIFETIME, type Pickup } from "../entity/pickup.js";
import { makeBoat, type Boat } from "../entity/boat.js";
import type { SaveGame } from "./saveGame.js";
import { SAVE_FORMAT_VERSION, WORLDGEN_VERSION } from "./saveGame.js";

export function captureSave(
  world: World,
  player: PlayerController,
  inventory: Inventory,
  craftGrid: Inventory,
  /** What is being worn, in ARMOUR_SLOTS order. */
  worn: Inventory,
  cycle: DayNightCycle,
  skinId: string,
  /** Where the player wakes, if they have slept somewhere. */
  spawn?: { x: number; y: number; z: number },
  /** Whatever is lying on the ground. Empty for a world with a clear floor. */
  ground: readonly Pickup[] = [],
  /** And whatever is floating on the water. */
  boats: readonly Boat[] = [],
  /** Пригоди already claimed. Empty on a world nobody has finished one in. */
  quests: ReadonlySet<string> = new Set(),
  /** Which лігва have had their Змій killed. See `SaveGame.slainLairs`. */
  slainLairs: ReadonlySet<string> = new Set(),
  /** How far down the шопопало-блок's table this world has got. */
  oneBlockBreaks = 0,
): SaveGame {
  return {
    formatVersion: SAVE_FORMAT_VERSION,
    savedAt: Date.now(),
    ...(oneBlockBreaks > 0 ? { oneBlockBreaks } : {}),
    world: {
      seed: world.opts.seed,
      sizeXZ: world.opts.sizeXZ,
      sizeY: world.opts.sizeY,
      // Which country it is, not only how big. Without these two a saved
      // hill came back as islands and a saved кремль came back without one.
      ...(world.opts.terrain !== undefined ? { terrain: world.opts.terrain } : {}),
      kremlin: world.opts.kremlin === true,
    },
    chunks: world.modifiedChunks().map((c) => ({
      cx: c.cx, cy: c.cy, cz: c.cz,
      // Copy: the live chunk keeps changing while the save is written.
      blocks: Uint16Array.from(c.blocks),
    })),
    worldgen: WORLDGEN_VERSION,
    quests: [...quests],
    slainLairs: [...slainLairs],
    containers: world.containers.save(),
    furnaces: world.furnaces.save(),
    ground: ground.map((p) => ({
      x: p.x, y: p.y, z: p.z, id: p.stack.id, count: p.stack.count,
      // Only when it differs, so an ordinary save says nothing new.
      ...(p.keepFor !== PICKUP_LIFETIME ? { keepFor: p.keepFor } : {}),
    })),
    boats: boats.map((b) => ({ x: b.x, y: b.y, z: b.z, yaw: b.yaw })),
    player: {
      x: player.position.x,
      y: player.position.y,
      z: player.position.z,
      yaw: player.yaw,
      pitch: player.pitch,
      mode: player.mode,
    },
    inventory: inventory.slots.map((s) => (s === null ? null : { ...s })),
    craftGrid: craftGrid.slots.map((s) => (s === null ? null : { ...s })),
    armour: worn.slots.map((s) => (s === null ? null : { ...s })),
    time: cycle.t,
    skinId,
    ...(spawn !== undefined ? { spawn: { ...spawn } } : {}),
  };
}

/**
 * Apply a save to an already-generated world.
 *
 * The world must have been created with the save's own options and generated
 * first: a save holds only the player's changes, so the terrain underneath has
 * to be regenerated from the same seed before the differences are laid on top.
 */
export function restoreSave(
  save: SaveGame,
  world: World,
  player: PlayerController,
  inventory: Inventory,
  craftGrid: Inventory,
  worn: Inventory,
  cycle: DayNightCycle,
  /** Filled with whatever the save had lying on the ground. */
  ground?: Pickup[],
  /** And with whatever it had afloat. */
  boats?: Boat[],
): void {
  for (const c of save.chunks) {
    world.loadChunk(c.cx, c.cy, c.cz, c.blocks);
  }

  // What the chests held. Independent of the chunks above - loadChunk writes
  // into the chunk directly rather than through setBlock, so it never trips
  // the rule that empties a cell which stops being a chest.
  world.containers.load(save.containers);
  world.furnaces.load(save.furnaces);

  if (boats !== undefined) {
    // Fresh ids: a save holds where each boat is, not what this session
    // happened to number it, and two worlds opened in one page would
    // otherwise be handing out the same numbers to different boats.
    boats.length = 0;
    for (const b of save.boats) {
      boats.push(makeBoat({ x: b.x, y: b.y, z: b.z }, b.yaw));
    }
  }

  if (ground !== undefined) {
    ground.length = 0;
    for (const g of save.ground) {
      // No delay: these have been lying there since before the game closed,
      // and making the player wait out a throw delay on load would be absurd.
      ground.push(makePickup(
        { id: g.id, count: g.count }, g, undefined, 0,
        g.keepFor ?? PICKUP_LIFETIME,
      ));
    }
  }

  player.position.x = save.player.x;
  player.position.y = save.player.y;
  player.position.z = save.player.z;
  player.velocity.x = 0;
  player.velocity.y = 0;
  player.velocity.z = 0;
  player.yaw = save.player.yaw;
  player.pitch = save.player.pitch;
  player.mode = save.player.mode;

  /*
   * And the ground under them, before anything gets a frame to fall through.
   *
   * This belongs here rather than in the caller because it is part of what
   * restoring a save *means*: the world has to be ready where the player is,
   * and the only thing that knows where that is, is this function. The
   * adventure is the only map grown a column at a time; the кремль and the
   * hill are whole the moment they are asked for.
   */
  if (world.opts.terrain === "adventure") {
    world.ensureAround(player.position.x, player.position.z, LOAD_REACH,
      Number.POSITIVE_INFINITY);
  }

  /*
   * And a player who is already in the void is put back on the ground.
   *
   * The fix above stops this happening; it does nothing for the saves it has
   * already happened to. Somebody who fell out of the world and quit has a
   * file that says they are at y minus four hundred, and loading it faithfully
   * puts them back there to carry on falling.
   *
   * Only when there is *nothing* solid underneath them anywhere in the
   * world. Standing in a cave, down a shaft or inside a building all have
   * floors; the void does not, and that is the whole difference.
   */
  const feet = {
    x: Math.floor(player.position.x),
    y: Math.floor(player.position.y),
    z: Math.floor(player.position.z),
  };
  let footing = false;
  for (let y = Math.min(feet.y - 1, world.opts.sizeY * 16 - 1); y > 0; y--) {
    if (isSolid(world.getBlock(feet.x, y, feet.z))) { footing = true; break; }
  }
  if (!footing) {
    // `groundAt` and not `surfaceAt`: the topmost block of any kind is
    // usually a leaf twenty blocks up, and setting somebody down in a
    // canopy drops them straight out of it.
    player.position.y = world.groundAt(feet.x, feet.z) + 1;
    player.velocity.y = 0;
  }

  // Items that have been retired since the save was written become whatever
  // replaced them, rather than a slot full of "?" the player cannot use.
  const restore = (target: Inventory, slots: SaveGame["inventory"]): void => {
    target.clear();
    slots.forEach((stack, i) => {
      target.set(i, stack === null
        ? null
        : { id: migrateItemId(stack.id), count: stack.count });
    });
  };

  restore(inventory, save.inventory);
  restore(craftGrid, save.craftGrid);
  restore(worn, save.armour);


  cycle.t = save.time;
}
